/**
 * useInsightsData Hook
 *
 * Runs the full computation pipeline once per assessment and caches results.
 * All Insights pages consume this hook for their data.
 */

import { useMemo } from 'react'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { calculateScores, calculateBenchmarkGap, calculateRiskMaturityAlignment, getEnhancedGapAnalysis } from '../../../shared/utils/scoring'
import { calculateTAPS, getThreatContextSummary } from '../../../shared/utils/threatScoring'
import { calculateLossScenarios, assessInsuranceAdequacy } from '../../../shared/utils/impactCalibration'
import { generateRoadmap } from '../../../shared/utils/roadmapGenerator'
import { generateEnablementPlan } from '../../../shared/utils/enablementPlanGenerator'
import { getMarketSegmentId } from '../../../shared/utils/marketSegment'
import { calculateFrameworkCoverage } from '../../../shared/utils/frameworkCoverageCalculator'
import { calculateDynamicCalibration } from '../../../shared/utils/dynamicScenarioCalibration'
import { simulateAggregateExposure } from '../../../shared/utils/monteCarloSimulation'
import { calculateInsuranceAdequacy as calcInsAdequacy } from '../../../shared/utils/insuranceAdequacy'
import { generatePlanBreakdown } from '../../../shared/utils/planPhaseBreakdown'
import { aggregateReportData } from '../../../shared/utils/reportDataAggregator'
import { resolveThreatProfile } from '../../../shared/utils/resolveThreatProfile'
import { applyClientIntelligenceOverlays } from '../../../shared/utils/threatProfileOverlays'

export function useInsightsData(assessmentId, {
  criticalSystems = null,
  enrichmentRow = null,
  technologyStack = [],
  controlTestData = null,
  enablementConfig = null,
  insuranceReview = null
} = {}) {
  const { getAssessment } = useAssessment()
  const { assessment, client } = getAssessment(assessmentId) || {}

  const result = useMemo(() => {
    if (!assessment?.responses?.maturityAssessment || !client) {
      return { data: null, raw: null, loading: false, error: null }
    }

    try {
      const responses = assessment.responses
      const industry = responses.businessScoping?.['bs-0']?.value || client.industry || 'Other'
      const marketSegment = getMarketSegmentId(responses.businessScoping)

      // Core scoring
      const scores = calculateScores(responses)
      const benchmarkData = calculateBenchmarkGap(scores.overall.score, industry, marketSegment)
      const alignments = calculateRiskMaturityAlignment(scores.riskAppetite, scores.maturity)
      const gapAnalysis = alignments ? getEnhancedGapAnalysis(alignments, industry, responses.businessScoping) : null

      // Threat context — single source of truth via resolveThreatProfile.
      // Cascade: saved profileKey → industry-driven recommendation → TP-DEFAULT.
      const { profile: selectedProfile, source: profileSource } = resolveThreatProfile({ client, responses })
      const threatContextSummary = selectedProfile ? getThreatContextSummary(selectedProfile, responses.threatContext || responses.notes?.threatContext) : null

      // Client Intelligence overlay — tunes the baseline profile with
      // enrichment-derived signals and surfaces named threat actors.
      const overlay = applyClientIntelligenceOverlays({
        profile: selectedProfile,
        enrichmentRow,
        businessScoping: responses.businessScoping,
        technologyStack
      })

      // TAPS
      const threatContext = {
        threat_actor_weights: responses.threatContext?.actorWeights || responses.notes?.threatContext?.actorWeights || {},
        attack_vector_weights: responses.threatContext?.vectorWeights || responses.notes?.threatContext?.vectorWeights || {},
        target_asset_weights: responses.threatContext?.assetWeights || responses.notes?.threatContext?.assetWeights || {}
      }
      const rawImpactData = responses.impactData || responses.notes?.impactData || {}
      // Enrich with critical systems BIA data when available
      let impactData = criticalSystems?.length
        ? { ...rawImpactData, criticalSystems }
        : rawImpactData

      // Overlay the latest cyber Insurance Policy Review onto impact data so
      // the AI-extracted aggregate limit, sublimits, and exclusions feed
      // Monte Carlo / Adequacy / Enablement Plan downstream. The reviewed
      // policy is the authoritative source — fall back to the questionnaire
      // value only if no review has been run.
      if (insuranceReview) {
        const reviewLimit = parseFloat(insuranceReview.coverage_limit_aggregate) || 0
        impactData = {
          ...impactData,
          // Coverage / retention — review data wins when present
          insurance_coverage_limit: reviewLimit > 0 ? reviewLimit : impactData.insurance_coverage_limit,
          insuranceCoverageLimit: reviewLimit > 0 ? reviewLimit : impactData.insuranceCoverageLimit,
          client_retention: insuranceReview.retention || impactData.client_retention,
          deductible: insuranceReview.retention || impactData.deductible,
          has_cyber_insurance: reviewLimit > 0 ? true : impactData.has_cyber_insurance,
          // Structured review output — read by Adequacy + Monte Carlo
          insuranceSublimits: insuranceReview.sublimits || {},
          insuranceExclusions: insuranceReview.notable_exclusions || [],
          insuranceReviewFindings: insuranceReview.findings || null,
          insuranceReviewId: insuranceReview.id || null,
          insuranceReviewedAt: insuranceReview.reviewed_at || null
        }
      }

      // Apply DISCOVER-source signal boosts to gap analysis before TAPS:
      //   1) Control Testing failures → increase the gap score for the
      //      mapped category (failed tests are concrete evidence of weakness)
      //   2) Critical Systems BIA tier → boost categories tied to systems
      //      flagged 'critical' (operations & data domains carry more weight)
      const enrichedGapAnalysis = applyDiscoverSignalsToGap(gapAnalysis, { controlTestData, criticalSystems })

      const tapsResults = enrichedGapAnalysis && selectedProfile
        ? calculateTAPS(enrichedGapAnalysis, threatContext, impactData, selectedProfile)
        : null

      // Extract org characteristics and frameworks for loss scenario context
      const bs = responses.businessScoping || {}
      const orgChars = []
      const fwks = []
      if (bs['bs-6']?.value === 'yes' || bs['bs-6'] === 'yes') orgChars.push('publicly-traded')
      if (bs['bs-7']?.value === 'yes' || bs['bs-7'] === 'yes') orgChars.push('government-contractor')
      if (bs['bs-8']) orgChars.push('processes-payments')
      const fwkResponse = bs['bs-11'] || bs['bs-12']
      if (fwkResponse) {
        const selected = Array.isArray(fwkResponse) ? fwkResponse : (fwkResponse.value ? [fwkResponse.value] : [])
        selected.forEach(f => {
          const fl = f.toLowerCase()
          if (fl.includes('hipaa')) fwks.push('hipaa')
          if (fl.includes('pci')) fwks.push('pci-dss')
          if (fl.includes('gdpr')) fwks.push('gdpr')
          if (fl.includes('ccpa') || fl.includes('cpra')) fwks.push('ccpa')
          if (fl.includes('cmmc') || fl.includes('nist 800-171')) fwks.push('cmmc')
          if (fl.includes('sox')) fwks.push('sox')
        })
      }

      // Loss scenarios — full context pipeline
      let lossScenarios = null
      try {
        if (impactData && selectedProfile) {
          lossScenarios = calculateLossScenarios(
            impactData,
            responses.threatContext || responses.notes?.threatContext,
            selectedProfile,
            scores.overall.score,
            responses.businessScoping,
            orgChars,
            fwks,
            null, // sensitiveDataVolumes (not available in hook)
            responses.maturityAssessment
          )
        }
      } catch (e) { console.warn('[useInsightsData] Loss scenarios failed:', e?.message) }

      // Phase 2 utilities
      const dynamicCalibration = calculateDynamicCalibration(
        responses.maturityAssessment,
        responses.technologyScoping,
        [],
        responses.businessScoping
      )

      const monteCarloResults = lossScenarios?.scenarios
        ? simulateAggregateExposure(lossScenarios.scenarios, dynamicCalibration, impactData, 10000)
        : null

      const applicableFrameworks = client.regulatory_frameworks || client.regulatoryFrameworks || responses.businessScoping?.['rf-1']?.value || []
      const frameworkCoverage = calculateFrameworkCoverage(null, responses.maturityAssessment, applicableFrameworks)

      const insuranceAdequacy = calcInsAdequacy(impactData, lossScenarios || {}, monteCarloResults || {})

      // Roadmap and plan
      let roadmap = null
      let planBreakdown = null
      let enablementPlan = null
      try {
        if (tapsResults && enrichedGapAnalysis) {
          roadmap = generateRoadmap(tapsResults, impactData, enrichedGapAnalysis, responses.technologyScoping, null, responses.businessScoping)
          planBreakdown = generatePlanBreakdown(roadmap, 'strategic-partner')
          // Single canonical Enablement Plan — also feeds Investment Priorities
          enablementPlan = generateEnablementPlan({
            roadmap,
            gapAnalysis: enrichedGapAnalysis,
            lossScenarios,
            tapsResults,
            scores,
            threatContext: responses.threatContext || responses.notes?.threatContext,
            businessScopingResponses: responses.businessScoping,
            insuranceAdequacy,
            config: enablementConfig || {}
          })
        }
      } catch (e) { console.warn('[useInsightsData] Roadmap/Enablement plan generation failed:', e?.message) }

      // Engagement context
      const engagementContext = {
        primaryDriver: responses.businessScoping?.['ed-1']?.value,
        timeline: responses.businessScoping?.['ed-2']?.value,
        outcomes: responses.businessScoping?.['ed-3']?.value,
        urgencyLevel: null
      }

      // Aggregate
      const data = aggregateReportData({
        assessment,
        client,
        scores,
        benchmarkData,
        alignments,
        gapAnalysis: enrichedGapAnalysis,
        tapsResults,
        lossScenarios,
        monteCarloResults,
        roadmap,
        frameworkCoverage,
        insuranceAdequacy,
        dynamicCalibration,
        threatContextSummary,
        engagementContext,
        planBreakdown
      })

      return {
        data,
        raw: {
          scores,
          benchmarkData,
          alignments,
          gapAnalysis: enrichedGapAnalysis,
          tapsResults,
          lossScenarios,
          monteCarloResults,
          frameworkCoverage,
          dynamicCalibration,
          insuranceAdequacy,
          planBreakdown,
          threatContextSummary,
          roadmap,
          enablementPlan,
          // Tuned threat profile + named actor relevance from Client Intelligence overlays
          tunedThreatProfile: {
            profile: selectedProfile,
            source: profileSource,
            namedActors: overlay.namedActors,
            evidence: overlay.evidence,
            adjustments: overlay.adjustments,
            enrichmentAvailable: overlay.enrichmentAvailable
          }
        },
        loading: false,
        error: null
      }
    } catch (err) {
      console.error('[useInsightsData] Computation error:', err)
      return { data: null, raw: null, loading: false, error: err.message }
    }
  }, [assessment?.id, assessment?.responses, client?.id, criticalSystems, enrichmentRow, technologyStack, controlTestData, enablementConfig, insuranceReview])

  return {
    ...result,
    loading: !assessment && !result.error
  }
}

// Map a control-test category (Maester block / Secure Score category) onto
// the gap-analysis domain it most closely reflects. Coarse but defensible —
// failed Identity controls clearly belong to Security & Technology, failed
// Data controls to Data Protection & Privacy, etc. Anything we can't map
// falls back to Security & Technology as a generic catch-all.
function mapControlCategoryToDomain(category) {
  if (!category) return 'Security & Technology'
  const c = String(category).toLowerCase()
  if (c.includes('identity') || c.includes('eidsca') || c.includes('iam') || c.includes('authentication')) return 'Security & Technology'
  if (c.includes('data') || c.includes('information protection') || c.includes('dlp')) return 'Data Protection & Privacy'
  if (c.includes('device') || c.includes('endpoint') || c.includes('intune')) return 'Operational Resilience'
  if (c.includes('app') || c.includes('exchange') || c.includes('teams') || c.includes('sharepoint')) return 'Operational Resilience'
  if (c.includes('infrastructure') || c.includes('network')) return 'Security & Technology'
  if (c.includes('govern') || c.includes('compliance')) return 'Regulatory & Compliance'
  return 'Security & Technology'
}

// Apply DISCOVER-source signal boosts to gap analysis prior to TAPS.
//   - Control Testing failure rate per domain pushes the gap more negative
//     (capped at -10 of additional gap so a single failed scan can't
//     dominate the score).
//   - Critical Systems flagged 'critical' or 'high' on operations-related
//     domains add a small extra gap penalty per flagged system (capped at -8).
function applyDiscoverSignalsToGap(gapAnalysis, { controlTestData, criticalSystems }) {
  if (!gapAnalysis?.impacts) return gapAnalysis

  // Aggregate failed/total control tests by mapped domain
  const failuresByDomain = {}
  if (Array.isArray(controlTestData?.results)) {
    for (const r of controlTestData.results) {
      const domain = mapControlCategoryToDomain(r.category)
      if (!failuresByDomain[domain]) failuresByDomain[domain] = { failed: 0, total: 0 }
      failuresByDomain[domain].total++
      if (r.result === 'failed') failuresByDomain[domain].failed++
    }
  }

  // Count high-criticality systems by inferred domain (operations/data)
  const criticalSystemBoosts = {}
  if (Array.isArray(criticalSystems)) {
    for (const sys of criticalSystems) {
      const tier = sys?.criticality_tier
      if (tier !== 'critical' && tier !== 'high') continue
      const sensitivity = Array.isArray(sys?.data_sensitivity) ? sys.data_sensitivity.join(' ').toLowerCase() : ''
      const domain = sensitivity.includes('phi') || sensitivity.includes('pii') || sensitivity.includes('financial')
        ? 'Data Protection & Privacy'
        : 'Operational Resilience'
      criticalSystemBoosts[domain] = (criticalSystemBoosts[domain] || 0) + (tier === 'critical' ? 2 : 1)
    }
  }

  const adjustedImpacts = gapAnalysis.impacts.map(impact => {
    const ctf = failuresByDomain[impact.domain]
    let extraGap = 0
    if (ctf && ctf.total > 0) {
      const rate = ctf.failed / ctf.total
      extraGap += -Math.min(10, Math.round(rate * 10))  // up to -10 from control testing
    }
    if (criticalSystemBoosts[impact.domain]) {
      extraGap += -Math.min(8, criticalSystemBoosts[impact.domain] * 2)  // up to -8 from BIA
    }
    if (extraGap === 0) return impact
    const newGap = Math.max(-100, (impact.gap || 0) + extraGap)
    return {
      ...impact,
      gap: newGap,
      discoverSignals: {
        controlTestFailureRate: ctf ? Math.round((ctf.failed / ctf.total) * 100) : null,
        criticalSystemCount: criticalSystemBoosts[impact.domain] || 0,
        gapAdjustment: extraGap
      }
    }
  })

  return { ...gapAnalysis, impacts: adjustedImpacts }
}
